import type { AppSettings } from '../types';
import { beginStep, finishStep } from '../lib/trace';
import type { DataPort, SemanticPrompt } from './contract';
import { retryModelChunk } from './modelRetry';
import { embeddingVector } from './provider';
import { graphiteUserHeader } from './requestHeaders';
import { semanticConfig } from './semantic';

export const TEACHER_PROMPT_KEY = 'annotate.persona.v1';
export const TEACHER_PROMPT_VERSION = '2026-09-18.1';
export const TEACHER_SCHEMA_VERSION = 'graphite.teacher.v2';
export const TOKENIZER_VERSION = 'graphite.segmenter.ru.v1';

const SURFACE_CLASSES = ['semantic_content', 'referent', 'relation_cue', 'discourse_marker', 'function_word', 'punctuation', 'whitespace'] as const;
const SEMANTIC_KINDS = ['goal', 'state', 'emotion', 'belief', 'value', 'constraint', 'capability', 'decision', 'action', 'outcome', 'entity', 'context'] as const;
const STANCES = ['asserted', 'quoted', 'question', 'hypothesis'] as const;
const POLARITIES = ['positive', 'negative', 'neutral'] as const;
const TEMPORALITIES = ['past', 'present', 'future', 'habitual', 'unspecified'] as const;
const SOURCES = ['speaker', 'other_person', 'narrator', 'unknown'] as const;
const RELATIONS = ['causes', 'prevents', 'enables', 'motivates', 'results_in', 'evidence_for', 'corefers_to', 'temporally_before', 'contrasts_with'] as const;
const EVIDENCE = ['explicit', 'entailed', 'hypothesis'] as const;

export const DEFAULT_TEACHER_PROMPT = `Ты выполняешь воспроизводимую семантическую разметку русскоязычного текста одного человека для обучающего датасета Graphite.

Используй только входной текст. Не добавляй внешние знания, не смешивай сведения разных людей и не выводи причину из одного соседства фраз.

Выполни одновременно четыре задачи:
1. Предложи минимальный набор предметных измерений для этого текста. Переиспользуй одно измерение для эквивалентных смыслов. dimension.id имеет вид d1, d2; dimension.key — устойчивый ASCII snake_case.
2. Выдели минимальные смысловые единицы. Единицы могут пересекаться и иметь несколько dimension_ids. Границы quote_parts задавай точными короткими цитатами из входного текста. Сохраняй отрицание, модальность, субъекта, время и условие.
3. Построй отношения между единицами. Направление причинной связи: основание или причина → следствие. Не создавай связь из соседства. Для explicit укажи cue_quotes точными цитатами явных маркеров, если они есть. Различай explicit, entailed и hypothesis.

Не возвращай токены, индексы символов или coverage. Цитаты должны быть дословными фрагментами входного текста; если нужна составная единица, верни несколько quote_parts. Отдельно различай утверждение говорящего, цитату другого человека, вопрос, гипотезу, отрицание, прошлое, настоящее, будущее и регулярность. Верни только JSON по заданной schema без пояснений.`;

export type SurfaceToken = { id: number; startChar: number; endChar: number; text: string };
type TokenPart = { start_token: number; end_token: number };
type QuotePart = { quote: string };
export type TeacherResult = {
  dimensions: Array<{ id: string; key: string; name: string; description: string }>;
  coverage: Array<{ token_id: number; surface_class: typeof SURFACE_CLASSES[number] }>;
  units: Array<{ id: string; parts: TokenPart[]; semantic_kind: typeof SEMANTIC_KINDS[number]; dimension_ids: string[]; stance: typeof STANCES[number]; polarity: typeof POLARITIES[number]; temporality: typeof TEMPORALITIES[number]; source: typeof SOURCES[number] }>;
  relations: Array<{ id: string; from_unit_id: string; to_unit_id: string; relation: typeof RELATIONS[number]; evidence: typeof EVIDENCE[number]; cue_parts: TokenPart[] }>;
};
type TeacherQuoteResult = {
  dimensions: TeacherResult['dimensions'];
  units: Array<Omit<TeacherResult['units'][number], 'parts'> & { quote_parts: QuotePart[] }>;
  relations: Array<Omit<TeacherResult['relations'][number], 'cue_parts'> & { cue_quotes: QuotePart[] }>;
};

const partSchema = { type: 'object', additionalProperties: false, properties: { start_token: { type: 'integer', minimum: 0 }, end_token: { type: 'integer', minimum: 1 } }, required: ['start_token', 'end_token'] };
export const TEACHER_OUTPUT_SCHEMA = {
  type: 'object', additionalProperties: false,
  properties: {
    dimensions: { type: 'array', items: { type: 'object', additionalProperties: false, properties: { id: { type: 'string' }, key: { type: 'string' }, name: { type: 'string' }, description: { type: 'string' } }, required: ['id', 'key', 'name', 'description'] } },
    coverage: { type: 'array', items: { type: 'object', additionalProperties: false, properties: { token_id: { type: 'integer', minimum: 0 }, surface_class: { type: 'string', enum: SURFACE_CLASSES } }, required: ['token_id', 'surface_class'] } },
    units: { type: 'array', items: { type: 'object', additionalProperties: false, properties: {
      id: { type: 'string' }, parts: { type: 'array', minItems: 1, items: partSchema }, semantic_kind: { type: 'string', enum: SEMANTIC_KINDS },
      dimension_ids: { type: 'array', minItems: 1, items: { type: 'string' } }, stance: { type: 'string', enum: STANCES }, polarity: { type: 'string', enum: POLARITIES },
      temporality: { type: 'string', enum: TEMPORALITIES }, source: { type: 'string', enum: SOURCES },
    }, required: ['id', 'parts', 'semantic_kind', 'dimension_ids', 'stance', 'polarity', 'temporality', 'source'] } },
    relations: { type: 'array', items: { type: 'object', additionalProperties: false, properties: {
      id: { type: 'string' }, from_unit_id: { type: 'string' }, to_unit_id: { type: 'string' }, relation: { type: 'string', enum: RELATIONS }, evidence: { type: 'string', enum: EVIDENCE }, cue_parts: { type: 'array', items: partSchema },
    }, required: ['id', 'from_unit_id', 'to_unit_id', 'relation', 'evidence', 'cue_parts'] } },
  }, required: ['dimensions', 'coverage', 'units', 'relations'],
} as const;

const quotePartSchema = { type: 'object', additionalProperties: false, properties: { quote: { type: 'string', minLength: 1 } }, required: ['quote'] };
export const TEACHER_QUOTE_OUTPUT_SCHEMA = {
  type: 'object', additionalProperties: false,
  properties: {
    dimensions: { type: 'array', items: { type: 'object', additionalProperties: false, properties: { id: { type: 'string' }, key: { type: 'string' }, name: { type: 'string' }, description: { type: 'string' } }, required: ['id', 'key', 'name', 'description'] } },
    units: { type: 'array', items: { type: 'object', additionalProperties: false, properties: {
      id: { type: 'string' }, quote_parts: { type: 'array', minItems: 1, items: quotePartSchema }, semantic_kind: { type: 'string', enum: SEMANTIC_KINDS },
      dimension_ids: { type: 'array', minItems: 1, items: { type: 'string' } }, stance: { type: 'string', enum: STANCES }, polarity: { type: 'string', enum: POLARITIES },
      temporality: { type: 'string', enum: TEMPORALITIES }, source: { type: 'string', enum: SOURCES },
    }, required: ['id', 'quote_parts', 'semantic_kind', 'dimension_ids', 'stance', 'polarity', 'temporality', 'source'] } },
    relations: { type: 'array', items: { type: 'object', additionalProperties: false, properties: {
      id: { type: 'string' }, from_unit_id: { type: 'string' }, to_unit_id: { type: 'string' }, relation: { type: 'string', enum: RELATIONS }, evidence: { type: 'string', enum: EVIDENCE }, cue_quotes: { type: 'array', items: quotePartSchema },
    }, required: ['id', 'from_unit_id', 'to_unit_id', 'relation', 'evidence', 'cue_quotes'] } },
  }, required: ['dimensions', 'units', 'relations'],
} as const;

export function tokenizeRussian(text: string): SurfaceToken[] {
  const segmenter = new Intl.Segmenter('ru', { granularity: 'word' });
  let offset = 0;
  return Array.from(segmenter.segment(text), (segment, id) => {
    const length = Array.from(segment.segment).length;
    const token = { id, startChar: offset, endChar: offset + length, text: segment.segment };
    offset += length;
    return token;
  });
}

function normalizeQuoteChar(character: string) {
  return (/\s/u.test(character) ? ' ' : character).toLocaleLowerCase('ru-RU');
}

function trimNormalizedQuote(value: string) {
  return Array.from(value).map(normalizeQuoteChar).join('').trim();
}

function indexOfChars(haystack: string[], needle: string[]) {
  if (!needle.length || needle.length > haystack.length) return -1;
  const limit = haystack.length - needle.length;
  for (let start = 0; start <= limit; start++) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset++) if (haystack[start + offset] !== needle[offset]) {
      matched = false; break;
    }
    if (matched) return start;
  }
  return -1;
}

function boundedEditDistance(left: string[], right: string[], maximum: number) {
  if (Math.abs(left.length - right.length) > maximum) return maximum + 1;
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex++) {
    const current = [leftIndex];
    let rowMinimum = current[0];
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex++) {
      const cost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1;
      const value = Math.min(previous[rightIndex] + 1, current[rightIndex - 1] + 1, previous[rightIndex - 1] + cost);
      current[rightIndex] = value;
      rowMinimum = Math.min(rowMinimum, value);
    }
    if (rowMinimum > maximum) return maximum + 1;
    previous = current;
  }
  return previous[right.length];
}

function findQuoteRange(text: string, quote: string, label: string) {
  if (typeof quote !== 'string') throw new Error(`Некорректная цитата в ${label}`);
  const textChars = Array.from(text);
  const normalizedText = textChars.map(normalizeQuoteChar);
  const normalizedQuote = Array.from(trimNormalizedQuote(quote)).map(normalizeQuoteChar);
  if (!normalizedQuote.length) throw new Error(`Пустая цитата в ${label}`);

  const exactStart = indexOfChars(normalizedText, normalizedQuote);
  if (exactStart >= 0) return { startChar: exactStart, endChar: exactStart + normalizedQuote.length };

  const maximumDistance = Math.floor(normalizedQuote.length * 0.05);
  if (maximumDistance <= 0) throw new Error(`Не удалось сопоставить цитату ${label} с исходным текстом: «${quote.slice(0, 80)}»`);
  let best: { startChar: number; endChar: number; distance: number } | undefined;
  const minimumLength = Math.max(1, normalizedQuote.length - maximumDistance);
  const maximumLength = Math.min(normalizedText.length, normalizedQuote.length + maximumDistance);
  for (let windowLength = minimumLength; windowLength <= maximumLength; windowLength++) {
    const limit = normalizedText.length - windowLength;
    for (let start = 0; start <= limit; start++) {
      const distance = boundedEditDistance(normalizedQuote, normalizedText.slice(start, start + windowLength), maximumDistance);
      if (distance <= maximumDistance && (!best || distance < best.distance)) best = { startChar: start, endChar: start + windowLength, distance };
      if (best?.distance === 0) return best;
    }
  }
  throw new Error(`Не удалось сопоставить цитату ${label} с исходным текстом: «${quote.slice(0, 80)}»`);
}

function partFromCharRange(tokens: SurfaceToken[], range: { startChar: number; endChar: number }, label: string): TokenPart {
  let startToken = tokens.findIndex((token) => token.endChar > range.startChar);
  let endToken = 0;
  for (let index = tokens.length - 1; index >= 0; index--) if (tokens[index].startChar < range.endChar) {
    endToken = index + 1; break;
  }
  while (startToken >= 0 && startToken < endToken && /^\s+$/u.test(tokens[startToken].text)) startToken++;
  while (endToken > startToken && /^\s+$/u.test(tokens[endToken - 1].text)) endToken--;
  if (startToken < 0 || endToken <= startToken) throw new Error(`Не удалось перевести цитату ${label} в токены`);
  return { start_token: startToken, end_token: endToken };
}

function coverageFromParts(tokens: SurfaceToken[], unitParts: TokenPart[], cueParts: TokenPart[]): TeacherResult['coverage'] {
  const cueTokenIds = new Set<number>();
  const semanticTokenIds = new Set<number>();
  for (const part of cueParts) for (let id = part.start_token; id < part.end_token; id++) cueTokenIds.add(id);
  for (const part of unitParts) for (let id = part.start_token; id < part.end_token; id++) semanticTokenIds.add(id);
  const relationCues = new Set(['потому', 'поэтому', 'значит', 'если', 'но', 'хотя']);
  const discourseMarkers = new Set(['получается', 'по', 'факту', 'сейчас', 'теперь', 'вообще', 'всё-таки']);
  const functionWords = new Set(['я', 'мы', 'мне', 'меня', 'мой', 'моя', 'моё', 'мои', 'он', 'она', 'оно', 'они', 'это', 'тот', 'та', 'то', 'те', 'в', 'во', 'на', 'с', 'со', 'к', 'ко', 'от', 'до', 'из', 'у', 'за', 'для', 'про', 'без', 'не', 'ни', 'ли', 'же', 'бы', 'и', 'а', 'но', 'что', 'чтобы', 'когда', 'куда', 'где', 'как', 'кто']);
  return tokens.map((token) => {
    if (/^\s+$/u.test(token.text)) return { token_id: token.id, surface_class: 'whitespace' as const };
    if (/^[\p{P}\p{S}]+$/u.test(token.text)) return { token_id: token.id, surface_class: 'punctuation' as const };
    const normalized = token.text.toLocaleLowerCase('ru-RU');
    if (cueTokenIds.has(token.id) || relationCues.has(normalized)) return { token_id: token.id, surface_class: 'relation_cue' as const };
    if (discourseMarkers.has(normalized)) return { token_id: token.id, surface_class: 'discourse_marker' as const };
    if (functionWords.has(normalized)) return { token_id: token.id, surface_class: 'function_word' as const };
    if (semanticTokenIds.has(token.id)) return { token_id: token.id, surface_class: 'semantic_content' as const };
    if (/^\p{Lu}/u.test(token.text)) return { token_id: token.id, surface_class: 'referent' as const };
    return { token_id: token.id, surface_class: 'semantic_content' as const };
  });
}

function materializeTeacherResult(value: TeacherQuoteResult, text: string, tokens: SurfaceToken[]): TeacherResult {
  if (!value || !Array.isArray(value.dimensions) || !Array.isArray(value.units) || !Array.isArray(value.relations)) throw new Error('LLM вернула неполную teacher-разметку');
  const unitParts: TokenPart[] = [];
  const units = value.units.map((unit) => {
    if (!Array.isArray(unit.quote_parts)) throw new Error(`Единица ${unit.id} не содержит quote_parts`);
    const parts = unit.quote_parts.map((part, index) => partFromCharRange(tokens, findQuoteRange(text, part.quote, `${unit.id}.quote_parts[${index}]`), `${unit.id}.quote_parts[${index}]`));
    unitParts.push(...parts);
    return {
      id: unit.id, parts, semantic_kind: unit.semantic_kind, dimension_ids: unit.dimension_ids,
      stance: unit.stance, polarity: unit.polarity, temporality: unit.temporality, source: unit.source,
    };
  });
  const cueParts: TokenPart[] = [];
  const relations = value.relations.map((relation) => {
    if (!Array.isArray(relation.cue_quotes)) throw new Error(`Связь ${relation.id} не содержит cue_quotes`);
    const parts = relation.cue_quotes.map((part, index) => partFromCharRange(tokens, findQuoteRange(text, part.quote, `${relation.id}.cue_quotes[${index}]`), `${relation.id}.cue_quotes[${index}]`));
    cueParts.push(...parts);
    return {
      id: relation.id, from_unit_id: relation.from_unit_id, to_unit_id: relation.to_unit_id,
      relation: relation.relation, evidence: relation.evidence, cue_parts: parts,
    };
  });
  return validateTeacherResult({ dimensions: value.dimensions, coverage: coverageFromParts(tokens, unitParts, cueParts), units, relations }, tokens);
}

function validatePart(part: TokenPart, tokenCount: number, label: string) {
  if (!Number.isInteger(part.start_token) || !Number.isInteger(part.end_token) || part.start_token < 0 || part.end_token <= part.start_token || part.end_token > tokenCount) {
    throw new Error(`LLM вернула неверные границы токенов (${label})`);
  }
}

export function validateTeacherResult(value: TeacherResult, tokens: SurfaceToken[]): TeacherResult {
  if (!value || !Array.isArray(value.dimensions) || !Array.isArray(value.coverage) || !Array.isArray(value.units) || !Array.isArray(value.relations)) throw new Error('LLM вернула неполную teacher-разметку');
  const dimensionIds = new Set(value.dimensions.map((dimension) => dimension.id));
  if (dimensionIds.size !== value.dimensions.length || value.dimensions.some((dimension) => !/^d[1-9]\d*$/.test(dimension.id) || !/^[a-z][a-z0-9_]{1,79}$/.test(dimension.key) || !dimension.name.trim())) throw new Error('LLM вернула некорректный справочник измерений');
  const coverageIds = value.coverage.map((item) => item.token_id).sort((left, right) => left - right);
  if (coverageIds.length !== tokens.length || coverageIds.some((id, index) => id !== index)) throw new Error(`Teacher-разметка не покрыла все токены: ${coverageIds.length}/${tokens.length}`);
  const unitIds = new Set<string>();
  for (const unit of value.units) {
    if (!/^u[1-9]\d*$/.test(unit.id) || unitIds.has(unit.id)) throw new Error('LLM вернула повторяющийся или неверный unit.id');
    unitIds.add(unit.id);
    unit.parts.forEach((part, index) => validatePart(part, tokens.length, `${unit.id}.parts[${index}]`));
    if (!unit.dimension_ids.length || unit.dimension_ids.some((id) => !dimensionIds.has(id))) throw new Error(`Единица ${unit.id} ссылается на неизвестное измерение`);
  }
  const relationIds = new Set<string>();
  for (const relation of value.relations) {
    if (!/^r[1-9]\d*$/.test(relation.id) || relationIds.has(relation.id) || relation.from_unit_id === relation.to_unit_id || !unitIds.has(relation.from_unit_id) || !unitIds.has(relation.to_unit_id)) throw new Error(`LLM вернула неверную связь ${relation.id}`);
    relationIds.add(relation.id);
    relation.cue_parts.forEach((part, index) => validatePart(part, tokens.length, `${relation.id}.cue_parts[${index}]`));
  }
  return value;
}

function responseText(body: Record<string, unknown>) {
  const choices = body.choices as Array<{ message?: { content?: string | Array<{ text?: string }> } }> | undefined;
  const content = choices?.[0]?.message?.content;
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) return content.map((part) => part.text || '').join('');
  throw new Error('LLM вернула пустой ответ');
}

async function sha256(text: string) {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(bytes), (value) => value.toString(16).padStart(2, '0')).join('');
}

async function teacherRequest(settings: AppSettings, spec: Pick<SemanticPrompt, 'template' | 'modelHint'>, text: string, tokens: SurfaceToken[], traceId?: string) {
  const model: 'gpt-5.4' | 'gpt-5.4-mini' = spec.modelHint === 'gpt-5.4-mini' ? 'gpt-5.4-mini' : 'gpt-5.4';
  const request = {
    model,
    reasoning_effort: 'high',
    messages: [{ role: 'system', content: spec.template }, { role: 'user', content: JSON.stringify({ text }) }],
    response_format: { type: 'json_schema', json_schema: { name: 'graphite_teacher_quotes', strict: true, schema: TEACHER_QUOTE_OUTPUT_SCHEMA } },
  };
  const stepId = traceId ? beginStep(traceId, `GPT-5.4 teacher · цитаты из ${tokens.length} токенов`, { endpoint: `${settings.llmEndpoint.replace(/\/$/, '')}/v1/chat/completions`, request }) : '';
  try {
    const completed = await retryModelChunk(async () => {
      const response = await fetch(`${settings.llmEndpoint.replace(/\/$/, '')}/v1/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${settings.serviceToken}`, 'X-Graphite-User': graphiteUserHeader(settings.userKey), ...(traceId ? { 'X-Graphite-Trace': traceId } : {}) },
        body: JSON.stringify(request),
      });
      if (!response.ok) throw new Error(`GPT-5.4 teacher недоступна (${response.status})`);
      const raw = responseText(await response.json() as Record<string, unknown>);
      return { raw, result: materializeTeacherResult(JSON.parse(raw) as TeacherQuoteResult, text, tokens) };
    });
    if (traceId) finishStep(traceId, stepId, { responseText: completed.raw, parsed: completed.result });
    return { model, result: completed.result };
  } catch (error) {
    if (traceId) finishStep(traceId, stepId, undefined, error);
    throw error;
  }
}

function charRange(tokens: SurfaceToken[], part: TokenPart) {
  return { startChar: tokens[part.start_token].startChar, endChar: tokens[part.end_token - 1].endChar - 1 };
}

export async function annotateSubmission(data: DataPort, settings: AppSettings, entryId: number, text: string, onProgress?: (value: string) => void, traceId?: string, jobId?: number) {
  const tokens = tokenizeRussian(text);
  if (!tokens.length) throw new Error('Нет токенов для разметки');
  const config = await semanticConfig();
  const configured = config.prompts.find((item) => item.key === TEACHER_PROMPT_KEY);
  const configuredTemplate = configured?.template.trim();
  const template = configuredTemplate?.includes('quote_parts') ? configuredTemplate : DEFAULT_TEACHER_PROMPT;
  const spec = { template, modelHint: configured?.modelHint || 'gpt-5.4' };
  onProgress?.(`GPT-5.4 выделяет цитаты из текста одним запросом…`);
  let annotated: { model: 'gpt-5.4' | 'gpt-5.4-mini'; result: TeacherResult };
  const cached = jobId !== undefined && data.getEnrichmentTask ? await data.getEnrichmentTask(jobId, 'llm:teacher:v2') : { completed: false };
  if (cached.completed) annotated = cached.result as typeof annotated;
  else {
    annotated = await teacherRequest(settings, spec, text, tokens, traceId);
    if (jobId !== undefined && data.completeEnrichmentTask) await data.completeEnrichmentTask(jobId, 'llm:teacher:v2', annotated);
  }

  const node = await data.createNode({ entryId, userId: settings.userId, kind: 'note', text, ordinal: 0, weight: 1 });
  await data.saveTeacherAnnotation({
    entryId, userId: settings.userId, sourceText: text, model: annotated.model, promptKey: TEACHER_PROMPT_KEY,
    promptVersion: `${TEACHER_PROMPT_VERSION}+${(await sha256(spec.template)).slice(0, 12)}`, schemaVersion: TEACHER_SCHEMA_VERSION, tokenizerVersion: TOKENIZER_VERSION,
    inputHash: await sha256(text), payload: { tokens, annotation: annotated.result },
  });

  const dimensions = new Map(annotated.result.dimensions.map((dimension) => [dimension.id, dimension]));
  const canonical = new Map<string, { id: number; dimensionKey: string; text: string }>();
  for (const unit of annotated.result.units) {
    for (const [partIndex, part] of unit.parts.entries()) {
      const range = charRange(tokens, part);
      const selectedDimensions = unit.dimension_ids.map((id) => dimensions.get(id)).filter((value): value is NonNullable<typeof value> => Boolean(value));
      for (const [dimensionIndex, dimension] of selectedDimensions.entries()) {
        const dimensionKey = `teacher:${dimension.key}`;
        const extraction = await data.createExtraction({ nodeId: node.id, dimensionKey, ...range });
        if (partIndex === 0 && dimensionIndex === 0) canonical.set(unit.id, { id: extraction.id, dimensionKey, text: Array.from(text).slice(range.startChar, range.endChar + 1).join('') });
      }
    }
  }
  for (const relation of annotated.result.relations) {
    const from = canonical.get(relation.from_unit_id); const to = canonical.get(relation.to_unit_id);
    if (from && to) await data.createEdge(from.id, to.id, relation.relation);
  }
  onProgress?.('Строим embeddings смысловых единиц…');
  for (const extraction of canonical.values()) {
    const vector = await embeddingVector(settings, extraction.text, traceId, `Teacher-unit ${extraction.dimensionKey}`);
    await data.putEmbedding('extraction', extraction.id, extraction.dimensionKey, vector);
  }
  onProgress?.(`Teacher-разметка сохранена: ${annotated.result.units.length} единиц, ${annotated.result.relations.length} связей`);
}
